import React from "react";
import { Table, Popconfirm, Tooltip } from "antd";
import type { ColumnType } from "antd/es/table";
import { Trash2, FileText, ArrowRightCircle, XCircle, Pencil, History, Link2 } from "lucide-react";
import dayjs from "dayjs";
import MyPagination from "../../../views/components/Pagination";
import { IPurchaseOrder, TPOStatus } from "../../../types/api/purchase-order";
import { QData } from "../../../types/basic-type-list";

interface POTableProps {
  data: IPurchaseOrder[];
  loading: boolean;
  qData: QData;
  refetch: (params: Partial<QData>) => void;
  DEFAULT_NUQS_CONFIG?: any;
  total: number;
  onEdit: (po: IPurchaseOrder) => void;
  onAdvanceStatus: (po: IPurchaseOrder) => void;
  onCancel: (po: IPurchaseOrder) => void;
  onDelete: (id: string) => void;
  onHistory?: (po: IPurchaseOrder) => void;
  onShare?: (id: number) => void;
  onDownloadPdf?: (po: IPurchaseOrder) => void;
}

const STATUS_CFG: Record<TPOStatus, { cls: string; label: string }> = {
  draft: {
    cls: "bg-slate-50 text-slate-500 border-slate-200",
    label: "Draft",
  },
  sent: {
    cls: "bg-blue-50 text-blue-700 border-blue-200",
    label: "Sent",
  },
  acknowledged: {
    cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    label: "Acknowledged",
  },
  partially_fulfilled: {
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    label: "Part. Fulfilled",
  },
  fulfilled: {
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    label: "Fulfilled",
  },
  cancelled: {
    cls: "bg-red-50 text-red-600 border-red-200",
    label: "Cancelled",
  },
};

// Statuses that allow advancing to next step
const ADVANCEABLE: TPOStatus[] = [
  "draft",
  "sent",
  "acknowledged",
  "partially_fulfilled",
];

function fmt(n: number | string | undefined | null) {
  return Number(n ?? 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
  });
}

const POTable: React.FC<POTableProps> = ({
  data,
  loading,
  qData,
  refetch,
  DEFAULT_NUQS_CONFIG,
  total,
  onEdit,
  onAdvanceStatus,
  onCancel,
  onDelete,
  onHistory,
  onShare,
  onDownloadPdf,
}) => {
  const columns: ColumnType<IPurchaseOrder>[] = [
    {
      title: "PO Number",
      dataIndex: "po_number",
      key: "po_number",
      render: (v) => (
        <span className="font-mono text-xs text-blue-600 font-semibold whitespace-nowrap">
          {v}
        </span>
      ),
    },
    {
      title: "Quotation",
      dataIndex: "quotation_number",
      key: "quotation_number",
      render: (v, row) => (
        <div>
          <div className="font-mono text-xs text-slate-500 font-semibold">
            {v ?? "—"}
          </div>
          {row.quotation_title && (
            <div className="text-[11px] text-slate-400 truncate max-w-[140px]">
              {row.quotation_title}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (v) => (
        <span className="font-medium text-slate-800 text-sm whitespace-nowrap">
          {v ?? <span className="text-slate-400 italic">Not set</span>}
        </span>
      ),
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (v) => (
        <span className="text-slate-600 text-sm whitespace-nowrap">
          {v ?? "—"}
        </span>
      ),
    },
    {
      title: "Issue Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (v) => (
        <span className="text-slate-500 text-xs whitespace-nowrap">
          {v ? dayjs(v).format("DD MMM YYYY") : "—"}
        </span>
      ),
    },
    {
      title: "Expected Delivery",
      dataIndex: "expected_delivery_date",
      key: "expected_delivery_date",
      render: (v) => {
        if (!v) return <span className="text-slate-400 text-xs">—</span>;
        const isOverdue = dayjs(v).isBefore(dayjs(), "day");
        return (
          <span
            className={`text-xs whitespace-nowrap ${isOverdue ? "text-red-500 font-medium" : "text-slate-500"}`}
          >
            {dayjs(v).format("DD MMM YYYY")}
            {isOverdue && (
              <span className="ml-1 text-[10px] text-red-400">(overdue)</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      align: "right",
      render: (v) => (
        <span className="text-slate-600 tabular-nums">{fmt(v)}</span>
      ),
    },
    {
      title: "VAT",
      dataIndex: "vat_amount",
      key: "vat_amount",
      align: "right",
      render: (v) => (
        <span className="text-slate-500 text-xs tabular-nums">{fmt(v)}</span>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (v) => (
        <span className="font-bold text-slate-800 tabular-nums">{fmt(v)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: TPOStatus) => {
        const { cls, label } = STATUS_CFG[v] || STATUS_CFG.draft;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, po) => {
        const canEdit = po.status === "draft";
        const canAdvance = ADVANCEABLE.includes(po.status);
        const canCancel =
          po.status !== "cancelled" && po.status !== "fulfilled";
        const canDelete = po.status === "draft";

        return (
          <div className="flex items-center justify-end gap-1.5">
            {onDownloadPdf && (
              <Tooltip title="Download PDF">
                <button
                  onClick={() => onDownloadPdf(po)}
                  className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors"
                >
                  <FileText size={13} className="text-red-600" />
                </button>
              </Tooltip>
            )}

            {canAdvance && (
              <Tooltip title="Advance status">
                <button
                  onClick={() => onAdvanceStatus(po)}
                  className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center hover:bg-blue-100 transition-colors"
                >
                  <ArrowRightCircle size={13} className="text-blue-600" />
                </button>
              </Tooltip>
            )}

            {canEdit && (
              <Tooltip title="Edit">
                <button
                  onClick={() => onEdit(po)}
                  className="w-7 h-7 bg-slate-50 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                >
                  <Pencil size={13} className="text-slate-500" />
                </button>
              </Tooltip>
            )}

            {canCancel && (
              <Tooltip title="Cancel PO">
                <button
                  onClick={() => onCancel(po)}
                  className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center hover:bg-amber-100 transition-colors"
                >
                  <XCircle size={13} className="text-amber-600" />
                </button>
              </Tooltip>
            )}

            {canDelete && (
              <Popconfirm
                title="Delete this Purchase Order?"
                description="This cannot be undone."
                okText="Delete"
                okType="danger"
                onConfirm={() => onDelete(String(po.id))}
              >
                <Tooltip title="Delete (draft only)">
                  <button className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors">
                    <Trash2 size={13} className="text-red-500" />
                  </button>
                </Tooltip>
              </Popconfirm>
            )}
            {onHistory && (
              <Tooltip title="Status History">
                <button
                  onClick={() => onHistory(po)}
                  className="w-7 h-7 bg-slate-50 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                >
                  <History size={13} className="text-slate-500" />
                </button>
              </Tooltip>
            )}
            {onShare && (
              <Tooltip title="Copy Share Link">
                <button
                  onClick={() => onShare(po.id)}
                  className="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center hover:bg-violet-100 transition-colors"
                >
                  <Link2 size={13} className="text-violet-600" />
                </button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey={(item) => item.id.toString()}
      pagination={false}
      loading={loading}
      scroll={{ x: "max-content" }}
      footer={() => (
        <MyPagination
          qData={qData}
          onChange={refetch}
          total={total}
          DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
        />
      )}
    />
  );
};

export default POTable;
